import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { loadStripe } from '@stripe/stripe-js';

// Initialize Stripe with user's live key
const stripePromise = loadStripe('pk_live_51SzAGh6lubi7TdZDC95RcUMJWYZgLnUcinGLTaKLVpEET0irPCWiSgb7HhzJcR13QahYPk9KPMdrJAaF8etCcj30008Cy3sHES');

const Upgrade = () => {
  const [tiers, setTiers] = useState([]);
  const [currentTier, setCurrentTier] = useState('free');
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('crypto'); // 'crypto' or 'stripe'
  const [cryptoAddresses, setCryptoAddresses] = useState({});
  const [showCryptoAddrs, setShowCryptoAddrs] = useState(false);

  useEffect(() => {
    fetchTiers();
    fetchCurrentSubscription();
  }, []);

  const fetchTiers = async () => {
    try {
      const response = await axios.get('/api/subscriptions/tiers');
      setTiers(response.data.tiers || []);
    } catch (err) {
      console.error('Failed to fetch tiers:', err);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await axios.get('/api/subscriptions/current');
      setCurrentTier(response.data.tier || 'free');
      if (response.data.crypto_addresses) {
        setCryptoAddresses(response.data.crypto_addresses);
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    }
  };

  const handleCryptoUpgrade = async () => {
    if (!selectedTier) return;
    
    setLoading(true);
    try {
      const response = await axios.post('/api/payments/crypto', {
        tier_name: selectedTier.name,
        crypto_type: selectedCrypto
      });
      setInvoice(response.data.invoice);
    } catch (err) {
      alert('Failed to create invoice: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleStripeCheckout = async () => {
    if (!selectedTier) return;
    
    setLoading(true);
    try {
      const response = await axios.post('/api/payments/stripe/create-checkout', {
        tier_name: selectedTier.name,
        price_usd: selectedTier.price_usd
      });
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (err) {
      alert('Failed to create checkout: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const cryptoIcons = {
    BTC: '₿',
    ETH: 'Ξ',
    SOL: '◎'
  };

  const cryptoColors = {
    BTC: '#F7931A',
    ETH: '#627EEA',
    SOL: '#14F195'
  };

  const cryptoNames = {
    BTC: 'Bitcoin',
    ETH: 'Ethereum',
    SOL: 'Solana'
  };

  return (
    <div className="fade-in">
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
          💎 Upgrade Your Account
        </h1>
        <p style={{ color: 'var(--text-muted)' }}>
          Choose your payment method - Credit Card or Cryptocurrency
        </p>
      </div>

      {/* Show User's Crypto Addresses */}
      {Object.keys(cryptoAddresses).length > 0 && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div 
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => setShowCryptoAddrs(!showCryptoAddrs)}
          >
            <div>
              <h3 style={{ marginBottom: '4px' }}>📬 Your Payment Addresses</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                Send crypto directly to upgrade instantly
              </p>
            </div>
            <span style={{ fontSize: '24px' }}>{showCryptoAddrs ? '▼' : '▶'}</span>
          </div>
          
          {showCryptoAddrs && (
            <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {cryptoAddresses.btc && (
                <div style={{ 
                  background: 'var(--darker)', 
                  padding: '16px', 
                  borderRadius: '8px',
                  border: '1px solid ' + cryptoColors.BTC
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '20px' }}>{cryptoIcons.BTC}</span>
                    <span style={{ fontWeight: '600' }}>Bitcoin (BTC)</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <code style={{ 
                      background: 'rgba(0,0,0,0.3)', 
                      padding: '8px 12px', 
                      borderRadius: '6px',
                      fontSize: '12px',
                      wordBreak: 'break-all',
                      flex: '1'
                    }}>
                      {cryptoAddresses.btc}
                    </code>
                    <button 
                      className="btn btn-ghost" 
                      style={{ fontSize: '12px', padding: '8px 12px' }}
                      onClick={() => copyToClipboard(cryptoAddresses.btc)}
                    >
                      📋 Copy
                    </button>
                  </div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
                    Pro: $5 | Enterprise: $20
                  </p>
                </div>
              )}

              {cryptoAddresses.eth && (
                <div style={{ 
                  background: 'var(--darker)', 
                  padding: '16px', 
                  borderRadius: '8px',
                  border: '1px solid ' + cryptoColors.ETH
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '20px' }}>{cryptoIcons.ETH}</span>
                    <span style={{ fontWeight: '600' }}>Ethereum (ETH)</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <code style={{ 
                      background: 'rgba(0,0,0,0.3)', 
                      padding: '8px 12px', 
                      borderRadius: '6px',
                      fontSize: '12px',
                      wordBreak: 'break-all',
                      flex: '1'
                    }}>
                      {cryptoAddresses.eth}
                    </code>
                    <button 
                      className="btn btn-ghost" 
                      style={{ fontSize: '12px', padding: '8px 12px' }}
                      onClick={() => copyToClipboard(cryptoAddresses.eth)}
                    >
                      📋 Copy
                    </button>
                  </div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
                    Pro: $5 | Enterprise: $20
                  </p>
                </div>
              )}

              {cryptoAddresses.sol && (
                <div style={{ 
                  background: 'var(--darker)', 
                  padding: '16px', 
                  borderRadius: '8px',
                  border: '1px solid ' + cryptoColors.SOL
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '20px' }}>{cryptoIcons.SOL}</span>
                    <span style={{ fontWeight: '600' }}>Solana (SOL)</span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <code style={{ 
                      background: 'rgba(0,0,0,0.3)', 
                      padding: '8px 12px', 
                      borderRadius: '6px',
                      fontSize: '12px',
                      wordBreak: 'break-all',
                      flex: '1'
                    }}>
                      {cryptoAddresses.sol}
                    </code>
                    <button 
                      className="btn btn-ghost" 
                      style={{ fontSize: '12px', padding: '8px 12px' }}
                      onClick={() => copyToClipboard(cryptoAddresses.sol)}
                    >
                      📋 Copy
                    </button>
                  </div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
                    Pro: $5 | Enterprise: $20
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {invoice ? (
        <div className="card" style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ marginBottom: '20px' }}>Payment Invoice</h2>
          
          <div style={{ 
            background: 'var(--darker)', 
            padding: '20px', 
            borderRadius: '12px',
            marginBottom: '20px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>
              {cryptoIcons[invoice.crypto_type]}
            </div>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>
              {invoice.amount} {invoice.crypto_type}
            </div>
            <div style={{ color: 'var(--text-muted)' }}>
              ≈ ${invoice.amount_usd} USD
            </div>
          </div>

          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
            <QRCodeSVG value={invoice.payment_address} size={200} />
          </div>

          <div style={{ 
            background: 'var(--darker)', 
            padding: '16px', 
            borderRadius: '8px',
            marginBottom: '20px',
            wordBreak: 'break-all',
            fontFamily: 'monospace',
            fontSize: '13px'
          }}>
            {invoice.payment_address}
          </div>

          <button 
            className="btn btn-secondary"
            onClick={() => copyToClipboard(invoice.payment_address)}
          >
            📋 Copy Address
          </button>

          <p style={{ marginTop: '20px', color: 'var(--text-muted)', fontSize: '14px' }}>
            Send exactly {invoice.amount} {invoice.crypto_type} to the address above.
            Your account will be upgraded automatically once confirmed.
          </p>
        </div>
      ) : (
        <>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
            marginBottom: '32px'
          }}>
            {tiers.filter(t => t.price_usd > 0).map((tier) => (
              <div 
                key={tier.name}
                className="card"
                style={{
                  borderColor: selectedTier?.name === tier.name ? '#DC2626' : 'var(--border)',
                  cursor: currentTier === tier.name ? 'default' : 'pointer',
                  position: 'relative',
                  opacity: currentTier === tier.name ? 0.7 : 1
                }}
                onClick={() => currentTier !== tier.name && setSelectedTier(tier)}
              >
                {currentTier === tier.name && (
                  <div className="badge badge-success" style={{ position: 'absolute', top: '16px', right: '16px' }}>
                    Current
                  </div>
                )}
                
                <h3 style={{ marginBottom: '8px', textTransform: 'capitalize' }}>{tier.name}</h3>
                <div style={{ fontSize: '36px', fontWeight: '800', color: '#DC2626', marginBottom: '16px' }}>
                  ${tier.price_usd}<span style={{ fontSize: '16px', color: 'var(--text-muted)' }}>/mo</span>
                </div>

                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#10B981' }}>✓</span>
                    {tier.daily_send === 999999 ? 'Unlimited' : tier.daily_send} emails/day
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#10B981' }}>✓</span>
                    {tier.storage_mb}MB storage
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#10B981' }}>✓</span>
                    {tier.max_accounts} email accounts
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#10B981' }}>✓</span>
                    {tier.max_temp_aliases} temp aliases
                  </li>
                  {tier.pgp_encryption && (
                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#10B981' }}>✓</span>
                      PGP encryption
                    </li>
                  )}
                  {tier.custom_domain && (
                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#10B981' }}>✓</span>
                      Custom domain support
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>

          {selectedTier && (
            <div className="card">
              <h3 style={{ marginBottom: '20px' }}>Select Payment Method</h3>
              
              {/* Payment Method Tabs */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                <button
                  className="btn"
                  style={{
                    background: paymentMethod === 'stripe' ? '#635BFF' : 'var(--card)',
                    color: paymentMethod === 'stripe' ? 'white' : 'var(--text)',
                    border: `2px solid ${paymentMethod === 'stripe' ? '#635BFF' : 'var(--border)'}`,
                    flex: '1'
                  }}
                  onClick={() => setPaymentMethod('stripe')}
                >
                  💳 Credit Card (Stripe)
                </button>
                <button
                  className="btn"
                  style={{
                    background: paymentMethod === 'crypto' ? '#10B981' : 'var(--card)',
                    color: paymentMethod === 'crypto' ? 'white' : 'var(--text)',
                    border: `2px solid ${paymentMethod === 'crypto' ? '#10B981' : 'var(--border)'}`,
                    flex: '1'
                  }}
                  onClick={() => setPaymentMethod('crypto')}
                >
                  ₿ Cryptocurrency
                </button>
              </div>

              {paymentMethod === 'stripe' ? (
                <div>
                  <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>
                    Secure payment processing by Stripe. You'll be redirected to complete payment.
                  </p>
                  <button
                    className="btn btn-primary"
                    style={{ 
                      width: '100%', 
                      background: '#635BFF',
                      fontSize: '16px',
                      padding: '16px'
                    }}
                    onClick={handleStripeCheckout}
                    disabled={loading}
                  >
                    {loading ? '⏳ Creating Checkout...' : `💳 Pay $${selectedTier.price_usd} with Card`}
                  </button>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                    {['BTC', 'ETH', 'SOL'].map((crypto) => (
                      <button
                        key={crypto}
                        className="btn"
                        style={{
                          background: selectedCrypto === crypto ? cryptoColors[crypto] : 'var(--card)',
                          color: selectedCrypto === crypto ? 'white' : 'var(--text)',
                          border: `2px solid ${selectedCrypto === crypto ? cryptoColors[crypto] : 'var(--border)'}`,
                          flex: '1',
                          minWidth: '100px'
                        }}
                        onClick={() => setSelectedCrypto(crypto)}
                      >
                        <span style={{ fontSize: '20px' }}>{cryptoIcons[crypto]}</span>
                        {crypto}
                      </button>
                    ))}
                  </div>

                  <button
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    onClick={handleCryptoUpgrade}
                    disabled={loading}
                  >
                    {loading ? '⏳ Creating Invoice...' : `₿ Generate ${selectedCrypto} Invoice`}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Upgrade;

